import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {BehaviorSubject, catchError, map, Observable, of, shareReplay, switchMap} from 'rxjs';
import {
    FeatureFlagService,
    TRANSLATE,
    TranslatePipe,
    caseInsensitiveComparator,
    includesIgnoreCase,
    keyComparator,
} from '@brunata-angular/util';
import {
    ConfirmationDialogService,
    InputDropdownCheckboxesComponent,
    ModalInputService,
    SelectableItem,
    Sorting,
} from '@brunata-angular/ui';
import {ToastrService} from 'ngx-toastr';
import {SftpConnectionsService} from './sftp-connections.service';
import {SftpAuthenticationBadgeComponent} from '../shared/ui/sftp-authentication-badge/sftp-authentication-badge.component';
import {
    ExportTableColumn,
    ExportTableComponent,
} from '../shared/ui/export-table/export-table.component';
import {ExportRowActionsComponent} from '../shared/ui/export-row-actions/export-row-actions.component';
import {
    SftpConnection,
    SftpConnectionRow,
    sftpAuthenticationLabelKeys,
    sftpAuthenticationTypes,
    toSftpConnectionRow,
} from './sftp-connection.model';
import {
    SavedExportFilterOption,
    selectedOptionIds,
    toSelectableItems,
} from '../saved-exports/saved-exports-filters.model';
import {
    SftpConnectionModalComponent,
    sftpConnectionModalOptions,
} from './sftp-connection-modal/sftp-connection-modal.component';
import {SftpTestServerFilesModalComponent} from './sftp-test-server-files-modal/sftp-test-server-files-modal.component';
import {insertBreakableName} from '../shared/models/confirmation-message.model';

const defaultSorting: Sorting = {column: 'name', direction: 'asc'};

@Component({
    selector: 'app-sftp-connections',
    standalone: true,
    imports: [
        TranslatePipe,
        ReactiveFormsModule,
        InputDropdownCheckboxesComponent,
        SftpAuthenticationBadgeComponent,
        ExportTableComponent,
        ExportRowActionsComponent,
    ],
    template: `
        <div class="container-xxl mx-0 pb-4">
            <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <p class="text-secondary mb-0">
                    {{ 'sftpconnection_overview_description' | translate: 'online' : 'export' }}
                </p>

                <button
                    type="button"
                    class="btn btn-primary text-nowrap"
                    (click)="createSftpConnection()">
                    {{ 'sftpconnection_overview_create' | translate: 'online' : 'export' }}
                </button>
            </div>

            @if (sftpConnections()?.length === 0) {
                <div class="alert alert-web-blue">
                    {{ 'sftpconnection_overview_empty' | translate: 'online' : 'export' }}
                </div>
            } @else {
                <div class="d-flex justify-content-end mb-2">
                    <button
                        type="button"
                        class="btn btn-outline-primary d-inline-flex align-items-center gap-2 text-nowrap"
                        (click)="resetFilters()">
                        <i class="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
                        {{ 'savedexport_overview_filter_reset' | translate: 'online' : 'export' }}
                    </button>
                </div>

                <app-export-table
                    [columns]="columns()"
                    [currentSorting]="sorting()"
                    [loading]="sftpConnections() === null"
                    (sort)="onSort($event)">
                    <tr appExportTableFilters class="bg-light" [formGroup]="filters">
                        <td class="align-top">
                            <app-input-dropdown-checkboxes
                                formControlName="authenticationTypes"
                                [placeholder]="
                                    'sftpconnection_overview_filter_authentication_all'
                                        | translate: 'online' : 'export'
                                " />
                        </td>
                        <td class="align-top">
                            <input
                                type="search"
                                class="form-control sftp-connections-search"
                                formControlName="name"
                                [placeholder]="
                                    'sftpconnection_overview_search_placeholder'
                                        | translate: 'online' : 'export'
                                " />
                        </td>
                        <td class="align-top">
                            <input
                                type="search"
                                class="form-control sftp-connections-search"
                                formControlName="destination"
                                [placeholder]="
                                    'savedexport_deliverylog_search_destination_placeholder'
                                        | translate: 'online' : 'export'
                                " />
                        </td>
                        <td class="align-top"></td>
                        <td class="align-top"></td>
                    </tr>
                    @for (row of filteredConnections(); track row.connection.sftpConnectionId) {
                        <tr>
                            <td class="align-middle">
                                <app-sftp-authentication-badge
                                    [authenticationType]="row.connection.authenticationType" />
                            </td>
                            <td class="align-middle fw-bold">
                                <div class="text-truncate" [title]="row.connection.name">
                                    {{ row.connection.name }}
                                </div>
                            </td>
                            <td class="align-middle sftp-connection-destination">
                                <div
                                    class="text-truncate"
                                    [title]="row.hostLabel + row.baseFolderLabel">
                                    <span>{{ row.hostLabel }}</span
                                    ><span class="text-secondary">{{ row.baseFolderLabel }}</span>
                                </div>
                            </td>
                            <td class="align-middle">
                                @if (row.connection.usedByExportCount === 0) {
                                    <span class="text-secondary">
                                        {{
                                            'sftpconnection_overview_used_by_none'
                                                | translate: 'online' : 'export'
                                        }}
                                    </span>
                                } @else if (row.connection.usedByExportCount === 1) {
                                    {{
                                        'sftpconnection_overview_used_by_one'
                                            | translate: 'online' : 'export'
                                    }}
                                } @else {
                                    {{
                                        'sftpconnection_overview_used_by_many'
                                            | translate: 'online' : 'export' : [row.usedByCountText]
                                    }}
                                }
                            </td>
                            <td class="align-middle text-end text-nowrap">
                                <div class="d-flex justify-content-end align-items-center gap-3">
                                    @if (
                                        testServerFilesEnabled() &&
                                        row.connection.isTestServer === true
                                    ) {
                                        <button
                                            type="button"
                                            class="btn btn-outline-primary btn-sm"
                                            (click)="showTestServerFiles(row.connection)">
                                            {{
                                                'sftpconnection_overview_files_on_server'
                                                    | translate: 'online' : 'export'
                                            }}
                                        </button>
                                    }
                                    <app-export-row-actions
                                        (edit)="editSftpConnection(row.connection)"
                                        (delete)="deleteSftpConnection(row.connection)" />
                                </div>
                            </td>
                        </tr>
                    } @empty {
                        <tr>
                            <td [attr.colspan]="columns().length">
                                {{
                                    'sftpconnection_overview_no_matches'
                                        | translate: 'online' : 'export'
                                }}
                            </td>
                        </tr>
                    }
                </app-export-table>
            }
        </div>
    `,
    styles: `
        .sftp-connections-search {
            min-width: 12.5rem;
        }

        .sftp-connection-destination {
            font-family: var(--bs-font-monospace);
            font-size: 0.8125rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SftpConnectionsComponent {
    private readonly sftpConnectionsService = inject(SftpConnectionsService);
    private readonly modalInputService = inject(ModalInputService);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly toastrService = inject(ToastrService);
    private readonly translate = inject(TRANSLATE);
    private readonly featureFlagService = inject(FeatureFlagService);

    protected readonly testServerFilesEnabled = toSignal(
        this.featureFlagService.isFeatureEnabled$('agi-20667-sftp-test-server-files'),
        {initialValue: false},
    );

    private readonly reload$ = new BehaviorSubject<void>(undefined);

    private readonly sftpConnections$: Observable<SftpConnectionRow[]> = this.reload$.pipe(
        switchMap(() =>
            this.sftpConnectionsService.getSftpConnections().pipe(
                map(page => page.sftpConnections.map(toSftpConnectionRow)),
                catchError(() => of<SftpConnectionRow[]>([])),
            ),
        ),
        shareReplay(1),
    );

    protected readonly sftpConnections = toSignal(this.sftpConnections$, {initialValue: null});

    private readonly authenticationOptions: SavedExportFilterOption[] = sftpAuthenticationTypes.map(
        authenticationType => ({
            id: authenticationType,
            label: this.translate(
                sftpAuthenticationLabelKeys[authenticationType],
                'online',
                'export',
            ),
        }),
    );

    protected readonly filters = new FormGroup({
        authenticationTypes: new FormControl<SelectableItem[]>(
            toSelectableItems(this.authenticationOptions, []),
            {nonNullable: true},
        ),
        name: new FormControl('', {nonNullable: true}),
        destination: new FormControl('', {nonNullable: true}),
    });

    protected readonly sorting = signal(defaultSorting);

    private readonly filterValues = toSignal(
        this.filters.valueChanges.pipe(map(() => this.filters.getRawValue())),
        {initialValue: this.filters.getRawValue()},
    );

    protected readonly filteredConnections = computed(() => {
        const {authenticationTypes, name, destination} = this.filterValues();
        const selectedTypes = selectedOptionIds(this.authenticationOptions, authenticationTypes);
        const matching = (this.sftpConnections() ?? []).filter(
            row =>
                (selectedTypes.length === 0 ||
                    selectedTypes.includes(row.connection.authenticationType)) &&
                includesIgnoreCase(row.connection.name, name) &&
                includesIgnoreCase(`${row.hostLabel}${row.baseFolderLabel}`, destination),
        );
        return this.sorted(matching);
    });

    protected readonly columns = computed<ExportTableColumn[]>(() => [
        {
            label: this.translate('sftpconnection_modal_authentication_label', 'online', 'export'),
            width: '21%',
        },
        {
            label: this.translate('savedexport_savedexports_column_name', 'online', 'export'),
            sortColumn: 'name',
        },
        {
            label: this.translate('sftpconnection_overview_column_destination', 'online', 'export'),
            width: '22%',
        },
        {
            label: this.translate('sftpconnection_overview_column_used_by', 'online', 'export'),
            sortColumn: 'usedBy',
            width: '12%',
        },
        {
            label: this.translate('savedexport_savedexports_column_actions', 'online', 'export'),
            alignEnd: true,
            width: '20%',
        },
    ]);

    protected onSort(sorting: Sorting): void {
        this.sorting.set(sorting);
    }

    protected resetFilters(): void {
        this.filters.setValue({
            authenticationTypes: toSelectableItems(this.authenticationOptions, []),
            name: '',
            destination: '',
        });
        this.sorting.set(defaultSorting);
    }

    protected createSftpConnection(): void {
        this.openSftpConnectionModal(null);
    }

    protected editSftpConnection(sftpConnection: SftpConnection): void {
        this.openSftpConnectionModal(sftpConnection);
    }

    protected deleteSftpConnection(sftpConnection: SftpConnection): void {
        const messageKey =
            sftpConnection.usedByExportCount === 0
                ? 'sftpconnection_delete_warning'
                : 'sftpconnection_delete_warning_in_use';
        void this.confirmationDialogService
            .confirmWithSettings({
                title: this.translate('common_warning', 'online', 'common'),
                message: insertBreakableName(
                    this.translate(messageKey, 'online', 'export'),
                    sftpConnection.name,
                ),
                btnOkText: this.translate('common_Delete'),
                centered: true,
            })
            .then(confirmed => {
                if (!confirmed) {
                    return;
                }
                this.sftpConnectionsService
                    .deleteSftpConnection(sftpConnection.sftpConnectionId)
                    .subscribe(() => {
                        this.reload$.next();
                        this.toastrService.success(
                            this.translate('sftpconnection_delete_success', 'online', 'export'),
                        );
                    });
            });
    }

    protected showTestServerFiles(sftpConnection: SftpConnection): void {
        this.modalInputService.open(
            SftpTestServerFilesModalComponent,
            {sftpConnection},
            {size: 'xl', scrollable: true, centered: true},
        );
    }

    // Every export picks its destination by sftpConnectionId, so a rename automatically reaches
    // the exports already using the connection.
    private openSftpConnectionModal(sftpConnection: SftpConnection | null): void {
        this.modalInputService
            .open(SftpConnectionModalComponent, {sftpConnection}, sftpConnectionModalOptions)
            .closed$.subscribe(() => this.reload$.next());
    }

    private sorted(rows: SftpConnectionRow[]): SftpConnectionRow[] {
        const {column, direction} = this.sorting();
        const comparator =
            column === 'usedBy'
                ? keyComparator<SftpConnectionRow, number>(
                      row => row.connection.usedByExportCount,
                      direction,
                  )
                : caseInsensitiveComparator<SftpConnectionRow>(
                      row => row.connection.name,
                      direction,
                  );
        return [...rows].sort(comparator);
    }
}
